import type {
  CreatePaymentTerminalRequest,
  PaymentTerminalView,
  UpdatePaymentTerminalRequest,
} from 'shared-models/browser';
import {
  INITIAL_PINPAD,
  type PinpadFormState,
  type TerminalFormState,
} from './terminals-page.types';

// Summary: Maps a terminal row into an editable draft (password left blank; pinpads expanded).
// Details: docs/function-library/web/contexts-pages.md#terminals-page
export function buildEditDraftFromTerminal(terminal: PaymentTerminalView): TerminalFormState {
  return {
    tenantId: terminal.tenantId,
    name: terminal.name,
    terminalNumber: terminal.terminalNumber,
    terminalPassword: '',
    currencyType: terminal.currencyType ?? '1',
    isActive: terminal.isActive,
    pinpads:
      terminal.pinpads.length > 0
        ? terminal.pinpads.map((pinpad) => ({
            pinpadId: pinpad.pinpadId,
            label: pinpad.label ?? '',
            isActive: pinpad.isActive,
            tipEnabled: pinpad.tipEnabled ?? false,
          }))
        : [{ ...INITIAL_PINPAD }],
  };
}

// Summary: Drops empty pinpad rows and trims identifiers before submit.
// Details: docs/function-library/web/contexts-pages.md#terminals-page
function normalizePinpads(pinpads: PinpadFormState[]) {
  return pinpads
    .map((pinpad) => ({
      pinpadId: pinpad.pinpadId.trim(),
      label: pinpad.label.trim(),
      isActive: pinpad.isActive,
      tipEnabled: pinpad.tipEnabled,
    }))
    .filter((pinpad) => pinpad.pinpadId.length > 0)
    .map((pinpad) => ({
      pinpadId: pinpad.pinpadId,
      label: pinpad.label.length > 0 ? pinpad.label : undefined,
      isActive: pinpad.isActive,
      tipEnabled: pinpad.tipEnabled,
    }));
}

// Summary: Builds the create request body; currencyType is optional and omitted when blank.
// Details: docs/function-library/web/contexts-pages.md#terminals-page
export function buildCreatePayload(form: TerminalFormState): CreatePaymentTerminalRequest {
  const currencyType = form.currencyType.trim();
  return {
    name: form.name.trim(),
    terminalNumber: form.terminalNumber.trim(),
    terminalPassword: form.terminalPassword,
    currencyType: currencyType.length > 0 ? currencyType : undefined,
    pinpads: normalizePinpads(form.pinpads),
  };
}

// Summary: Builds the update request body; password is sent only when rotated.
// Details: docs/function-library/web/contexts-pages.md#terminals-page
export function buildUpdatePayload(form: TerminalFormState): UpdatePaymentTerminalRequest {
  const currencyType = form.currencyType.trim();
  return {
    name: form.name.trim(),
    terminalNumber: form.terminalNumber.trim(),
    terminalPassword: form.terminalPassword.length > 0 ? form.terminalPassword : undefined,
    currencyType: currencyType.length > 0 ? currencyType : undefined,
    isActive: form.isActive,
    pinpads: normalizePinpads(form.pinpads),
  };
}
